import { useT } from '@hana/i18n';
import '../i18n';
import { useEffect, useId, useMemo, useState } from 'react';
import {
  IconAdjustmentsHorizontal, IconAlarm, IconArchive, IconArrowBackUp, IconArrowBackUpDouble, IconArrowForwardUp, IconBell, IconBuilding, IconCheck, IconChevronDown,
  IconDots, IconDownload, IconExternalLink, IconFolder, IconInbox, IconLayoutSidebarRight, IconMail, IconMenu2, IconMessages, IconPaperclip, IconPencil, IconPlus,
  IconSearch, IconSparkles, IconEye, IconRefresh, IconTag, IconTrash, IconUserPlus, IconFolderPlus, IconSend, IconNotes, IconAlertCircle,
} from '@tabler/icons-react';
import { Page, useShell } from '@hana/shell';
import { Segmented, useToast, type ScreenState } from '@hana/ui';
import { ATT_MB, MAILS, threadOf, unreadCount, type BoxMail } from '../data';
import { attTotal, boxTitle, filterMails, groupByDay, initials, inboxOf, jobCard, slaLabel, slaShort, unassignedCount, UNASSIGNED, type MailFilter } from './view';
import { FolderTree } from './FolderTree';
import { BoxPad, BoxPhone } from './BoxCompact';
import s from './box.module.css';

// Mint-Box (desktop 1536, `-1920`): hộp thư chung info@ · danh sách · đọc thư · bảng phải Job / Khách.
// Chỉ đọc, dữ liệu mẫu: nút gửi / lưu / giao PIC báo toast "Chưa mở … ở bản này" (docs/streams/I.md). Chưa làm: tab Khách, hộp khác ngoài info@,
// menu thả (thêm, soạn, thông báo), cửa sổ soạn thư, tìm nâng cao, bong bóng Hei (A lắp ở shell).

const NOT_YET = 'Chưa mở gửi ở bản này';

function readState(): ScreenState {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : 'ready';
}

/** Khổ ≥ 1800 (board `-1920`): cột hộp thư mở rộng, bảng phải gắn cố định (không nút đóng / mở). */
function useWide(): boolean {
  const q = '(min-width: 1800px)';
  const [w, setW] = useState(() => typeof matchMedia === 'function' && matchMedia(q).matches);
  useEffect(() => {
    const m = matchMedia(q);
    const f = () => setW(m.matches);
    m.addEventListener('change', f);
    return () => m.removeEventListener('change', f);
  }, []);
  return w;
}

const FILTERS: ReadonlyArray<{ key: MailFilter; label: string }> = [
  { key: 'all', label: 'Tất cả' },
  { key: 'open', label: 'Chưa xử lý' },
  { key: 'unread', label: 'Chưa đọc' },
];

/** Nút icon đứng sau nhãn ẩn: tên cho trình đọc màn hình, không lặp thành nhãn riêng trên màn. */
function RailBtn({ label, icon, dot, on }: { label: string; icon: React.ReactNode; dot?: boolean; on?: boolean }) {
  const id = useId();
  return (
    <button type="button" className={`${s.rail} ${on ? s.railOn : ''}`} aria-labelledby={id}>
      {icon}
      {dot && <span className={`${s.railDot} ${on ? s.railDotOn : ''}`} aria-hidden="true" />}
      <span id={id} hidden>{label}</span>
    </button>
  );
}

function Avatar({ pic, small }: { pic: string; small?: boolean }) {
  return (
    <span className={`${s.ava} ${small ? s.avaSm : ''}`} role="img" aria-label={`PIC ${pic}`}>
      {initials(pic)}
    </span>
  );
}

function MailRow({ m, on, pick }: { m: BoxMail; on: boolean; pick: () => void }) {
  const t = useT();
  const sla = slaShort(m);
  const urgent = !!m.k;
  return (
    <button type="button" className={`${s.row} ${on ? s.rowOn : ''}`} aria-current={on || undefined} onClick={pick}>
      <span className={s.rowDotCol}>{m.unread ? <span className={s.unread} /> : <span className={s.read} />}</span>
      <span className={s.rowBody}>
        <span className={s.rowTop}>
          <span className={s.from}>
            {m.from}
            {m.thread > 1 && <span className={s.cnt}>{m.thread}</span>}
          </span>
          <span className={s.time}>
            {m.att.length > 0 && <IconPaperclip size={13} stroke={1.9} aria-hidden="true" />}
            {m.time}
          </span>
        </span>
        <span className={`${s.subj} ${m.unread ? s.subjB : ''}`}>{m.subj}</span>
        <span className={s.snip}>{m.body[1] ?? m.body[0]}</span>
        <span className={s.meta}>
          {m.label && (
            <span className={`${s.tag} ${urgent ? s.tagErr : ''}`}>
              {!urgent && <IconTag size={12} stroke={1.9} aria-hidden="true" />}
              {m.label}
            </span>
          )}
          {m.pic === UNASSIGNED ? <span className={s.unassigned}><IconUserPlus size={12} stroke={1.9} aria-hidden="true" />{t('box.unassigned')}</span> : <Avatar pic={m.pic} small />}
          {m.job && (
            <span className={s.jobChip}>
              <IconExternalLink size={12} stroke={1.9} aria-hidden="true" />
              {m.job}
            </span>
          )}
          {m.kh && (
            <span className={s.kh}>
              <IconBuilding size={12} stroke={1.9} aria-hidden="true" />
              {m.kh}
            </span>
          )}
          {sla && (
            <span className={`${s.sla} ${(m.sla ?? 0) < 0 ? s.slaLate : ''}`} role="img" aria-label={`SLA ${slaLabel(m)}`}>
              <IconAlarm size={12} stroke={1.9} aria-hidden="true" />
              {sla}
            </span>
          )}
        </span>
      </span>
    </button>
  );
}

/** iPad (pad) và điện thoại (phone) dựng theo board riêng `Mint-Box-iPad` / `-Phone`; desktop theo `Mint-Box`. */
export function BoxPage() {
  const toast = useToast();
  const { layout } = useShell();
  const soon = () => toast({ msg: NOT_YET });
  if (layout === 'pad') return <BoxPad state={readState()} soon={soon} />;
  if (layout === 'phone') return <BoxPhone state={readState()} soon={soon} />;
  return <BoxDesktop />;
}

function BoxDesktop() {
  const t = useT();
  const toast = useToast();
  const state = readState();
  const [filter, setFilter] = useState<MailFilter>('all');
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(13);
  const [push, setPush] = useState(true);
  const [warn, setWarn] = useState(true);
  const [dockOpen, setDock] = useState(true);
  const wide = useWide();
  const dock = wide || dockOpen;
  const [tab, setTab] = useState<'job' | 'cus'>('job');
  const [ask, setAsk] = useState('');

  const inbox = useMemo(() => inboxOf('info'), []);
  const full = useMemo(() => filterMails(inbox, filter, q), [inbox, filter, q]);
  const list = state === 'empty' ? [] : full;
  const groups = useMemo(() => groupByDay(list), [list]);
  const cur = state === 'empty' ? undefined : full.find((m) => m.id === sel) ?? full[0];
  const ready = state === 'ready' || state === 'noperm';
  const soon = () => toast({ msg: NOT_YET });

  const job = cur?.job ? jobCard(cur.job) : null;
  return (
    <Page kind="tool">
    <div className={s.page}>
      <div className={s.main}>
        <header className={s.head}>
          <h1 className={s.sr}>HANA Box</h1>
          <Segmented label="Chế độ" value="mail" onChange={() => undefined} items={[{ key: 'mail', label: 'Thư', n: unreadCount(MAILS) }]} />
          <div className={s.mid}>
            <div className={s.search}>
              <IconSearch size={16} stroke={2.2} aria-hidden="true" />
              <input aria-label="Tìm thư (phím /)" placeholder="Tìm thư, người, JobID, booking…  /" value={q} onChange={(e) => setQ(e.target.value)} />
              <button type="button" className={s.ibtn} aria-label="Tìm nâng cao" title="Tìm nâng cao" onClick={soon}>
                <IconAdjustmentsHorizontal size={15} stroke={1.9} aria-hidden="true" />
              </button>
            </div>
            {ready && <div className={s.tools} role="toolbar" aria-label="Thao tác thư">
              <button type="button" className={s.tool} aria-label="Lưu trữ (E)" onClick={soon}><IconArchive size={16} stroke={1.9} aria-hidden="true" /></button>
              <button type="button" className={s.tool} aria-label="Xoá" onClick={soon}><IconTrash size={16} stroke={1.9} aria-hidden="true" /></button>
              <button type="button" className={s.tool} aria-label="Di chuyển tới…" onClick={soon}><IconFolder size={16} stroke={1.9} aria-hidden="true" /></button>
              <button type="button" className={s.tool} aria-label="Gắn nhãn" onClick={soon}><IconTag size={16} stroke={1.9} aria-hidden="true" /></button>
              <button type="button" className={s.tool} aria-label="Tạm ẩn" onClick={soon}><IconAlarm size={16} stroke={1.9} aria-hidden="true" /></button>
              <span className={s.sep} aria-hidden="true" />
              <button type="button" className={s.digest} onClick={soon}><IconNotes size={16} stroke={1.9} aria-hidden="true" />{t('box.threadSummary')}</button>
              <button type="button" className={s.tool} aria-label="Thêm thao tác: in, PDF, gắn job, cửa sổ riêng, Outlook" aria-haspopup="menu" onClick={soon}>⋯</button>
            </div>}
          </div>
          <div className={s.right}>
            <button type="button" className={s.find} aria-label="Tìm nhanh (Ctrl K)" title="Tìm nhanh · Ctrl K" onClick={soon}>
              <IconSearch size={16} stroke={1.9} aria-hidden="true" />
              Ctrl K
            </button>
            <button type="button" className={s.hb} aria-label="Thông báo, 4 chưa xem" title="Thông báo" onClick={soon}>
              <IconBell size={18} stroke={1.9} aria-hidden="true" />
              <span className={s.hbN}>4</span>
            </button>
            {!wide && (
              <button type="button" className={`${s.hb} ${dock ? s.hbOn : ''}`} aria-label="Thông tin job / khách" aria-pressed={dock} title="Thông tin job / khách" onClick={() => setDock((d) => !d)}>
                <IconLayoutSidebarRight size={18} stroke={1.9} aria-hidden="true" />
              </button>
            )}
            <button type="button" className={s.hb} aria-label="Thêm: bản tin, cài đặt, mật độ, phím tắt" aria-haspopup="menu" title="Thêm" onClick={soon}>
              <IconDots size={18} stroke={1.9} aria-hidden="true" />
            </button>
            <div className={s.split}>
              <button type="button" className={s.compose} onClick={soon}><IconPencil size={16} stroke={1.9} aria-hidden="true" /><span>{t('box.newMail')}</span></button>
              <button type="button" className={s.composeMore} aria-label="Soạn thư: chọn cách mở" aria-haspopup="menu" onClick={soon}><IconChevronDown size={15} stroke={2} aria-hidden="true" /></button>
            </div>
          </div>
        </header>

        {push && state === 'ready' && (
          <div role="status" className={s.push}>
            <IconBell size={16} stroke={1.9} aria-hidden="true" />
            <span className={s.grow}>{t('box.pushPrompt')}</span>
            <button type="button" className={s.pushOn} onClick={() => { setPush(false); soon(); }}><IconBell size={14} stroke={1.9} aria-hidden="true" />{t('box.turnOn')}</button>
            <button type="button" className={s.pushLater} onClick={() => setPush(false)}><IconAlarm size={14} stroke={1.9} aria-hidden="true" />{t('box.later')}</button>
          </div>
        )}

        <div className={s.cols}>
          <section className={`${s.c1} ${wide ? s.c1W : ''}`} aria-label="Hộp thư và nhãn">
            {wide ? <FolderTree /> : (<>
            <button type="button" className={s.expand} aria-label="Mở cột hộp thư (phím ])" onClick={soon}>»</button>
            <RailBtn label="Hộp thư đến" icon={<IconMenu2 size={16} stroke={1.9} aria-hidden="true" />} />
            <RailBtn label="Hộp cá nhân" icon={<IconInbox size={16} stroke={1.9} aria-hidden="true" />} dot />
            <RailBtn label="info@" icon={<IconMail size={16} stroke={1.9} aria-hidden="true" />} dot on />
            <RailBtn label="support@" icon={<IconMessages size={16} stroke={1.9} aria-hidden="true" />} dot />
            <RailBtn label="Nháp" icon={<IconPencil size={16} stroke={1.9} aria-hidden="true" />} />
            <RailBtn label="Hẹn giờ gửi" icon={<IconSend size={16} stroke={1.9} aria-hidden="true" />} dot />
            <RailBtn label="Nhãn" icon={<IconTag size={16} stroke={1.9} aria-hidden="true" />} dot />
            </>)}
          </section>

          <section className={s.c2} aria-label="Danh sách thư">
            <div className={s.lh}>
              <b>{boxTitle('info')}</b>
              <span className={s.lhR}>
                <span className={s.lhN}>{unassignedCount(inbox)} thư chưa gán PIC</span>
                <button type="button" className={s.lbtn} onClick={soon}>{t('box.select')}</button>
                <button type="button" className={s.lbtn} aria-label="Thêm thao tác danh sách" onClick={soon}>⋯</button>
              </span>
            </div>
            <div className={s.filters} role="group" aria-label="Lọc danh sách">
              {FILTERS.map((f) => (
                <button key={f.key} type="button" className={`${s.fchip} ${filter === f.key ? s.fchipOn : ''}`} aria-pressed={filter === f.key} onClick={() => setFilter(f.key)}>
                  {f.label}
                </button>
              ))}
              <span className={s.fsep} aria-hidden="true" />
              <button type="button" className={`${s.fchip} ${filter === 'mine' ? s.fchipOn : ''}`} aria-pressed={filter === 'mine'} onClick={() => setFilter(filter === 'mine' ? 'all' : 'mine')}>{t('box.mine')}</button>
              <button type="button" className={`${s.fchip} ${s.fplain}`} onClick={soon}><IconPlus size={12} stroke={2} aria-hidden="true" />{t('box.filter')}</button>
            </div>
            {warn && state === 'ready' && (
              <div className={s.warn} role="note">
                <IconAlertCircle size={15} stroke={2} aria-hidden="true" />
                <div className={s.warnTx}>
                  <b>{t('box.blOutside')}</b> {t('box.blOutsideDesc')} <a href="/ezy/documents">{t('box.openDocs')}</a>
                </div>
                <button type="button" className={s.warnX} aria-label="Bỏ qua cảnh báo" onClick={() => setWarn(false)}>×</button>
              </div>
            )}
            <div className={s.rows}>
              {state === 'loading' && [1, 2, 3, 4, 5, 6, 7, 8].map((k) => (
                <div key={k} className={s.skRow} aria-hidden="true"><i style={{ width: '40%' }} /><i style={{ width: '85%' }} /><i style={{ width: '60%' }} /></div>
              ))}
              {state === 'empty' && (
                <div className={s.stBody}>
                  <div className={s.stIc}><IconMail size={26} stroke={1.8} aria-hidden="true" /></div>
                  <b>{t('box.st.doneAll')}</b>
                  <span>{t('box.st.doneAllDesc')}</span>
                  <button type="button" className={s.stBtn} onClick={soon}><IconEye size={16} stroke={1.9} aria-hidden="true" />{t('box.st.viewDone')}</button>
                </div>
              )}
              {state !== 'loading' && state !== 'empty' && groups.map((g) => (
                <div key={g.day}>
                  <div className={s.grp}>{g.label}</div>
                  {g.rows.map((m) => (
                    <MailRow key={m.id} m={m} on={cur?.id === m.id} pick={() => setSel(m.id)} />
                  ))}
                </div>
              ))}
              {list.length === 0 && state !== 'empty' && state !== 'loading' && <div className={s.none}>{t('box.noMatch')}</div>}
            </div>
          </section>

          <section className={s.c3} aria-label="Đọc thư">
            {state === 'error' ? (
              <div className={`${s.stBody} ${s.stFill}`}>
                <div className={`${s.stIc} ${s.stErr}`}>!</div>
                <b>{t('box.st.errTitle')}</b>
                <span className={s.stMax}>{t('box.st.errDesc')}</span>
                <button type="button" className={`${s.stBtn} ${s.stBtnO}`} onClick={soon}><IconRefresh size={16} stroke={1.9} aria-hidden="true" />{t('box.retry')}</button>
              </div>
            ) : state === 'loading' ? (
              <div className={s.skRead} aria-busy="true" aria-label={t('box.st.loading')}>
                <i style={{ width: '60%', height: 20 }} /><i style={{ width: '35%' }} />
                {[1, 2, 3, 4, 5, 6, 7, 8].map((k) => <i key={k} />)}
              </div>
            ) : state === 'empty' ? null : cur ? <Reader m={cur} soon={soon} /> : <div className={s.none}>{t('box.pickMail')}</div>}
          </section>
        </div>
      </div>

      {dock && state !== 'error' && (
        <div className={s.dock} role="complementary" aria-label="Thông tin job và khách">
          <div className={s.dockHd}>
            <Segmented label="Thông tin" value={tab} onChange={setTab} items={[{ key: 'job', label: 'Job' }, { key: 'cus', label: 'Khách' }]} />
            {!wide && <button type="button" className={s.dockX} aria-label="Đóng bảng" onClick={() => setDock(false)}>×</button>}
          </div>
          {cur && ready && <section aria-label="Hana nhận diện từ thư" className={s.sec}>
            <div className={s.cap}>{t('box.detected')}</div>
            {cur.job && (
              <div className={s.match}>
                <span className={s.matchTx}>{t('box.matchJob')} <a href={`/ezy/jobs/${cur.job}`}>{cur.job}</a></span>
                <button type="button" className={s.jobAct} onClick={soon}><IconDots size={14} stroke={1.9} aria-hidden="true" />{t('box.jobActions')}<IconChevronDown size={13} stroke={2} aria-hidden="true" /></button>
              </div>
            )}
            <div className={s.sumBox}>
              <div className={s.sumHd}>
                <b>Ask HANA · tóm tắt luồng ({Math.max(1, threadOf(cur.id).length || cur.thread)} thư)</b>
                <div className={s.sumActs}>
                  <button type="button" className={s.sumBtn} onClick={soon}><IconPencil size={14} stroke={1.9} aria-hidden="true" />{t('box.compose')}</button>
                  <button type="button" className={s.sumBtn} onClick={soon}><IconPlus size={14} stroke={1.9} aria-hidden="true" />{t('box.createTask')}</button>
                </div>
              </div>
              <ul className={s.sum}>
                {cur.sum.map((x) => <li key={x}>{x}</li>)}
              </ul>
              <div className={s.askRow}>
                <input aria-label="Hỏi HANA về thư này" placeholder="Hỏi HANA về thư này, vd: hạn SI là khi nào?" value={ask} onChange={(e) => setAsk(e.target.value)} />
                <button type="button" className={s.askBtn} onClick={soon}><IconSparkles size={14} stroke={1.9} aria-hidden="true" />Ask HANA</button>
              </div>
            </div>
          </section>}
          {cur && job && (
            <>
              <section className={s.card}>
                <div className={s.jobHd}>
                  <a href={`/ezy/jobs/${job.no}`}>{job.no}</a>
                  <span className={s.jobSt}><span className={s.stDot} aria-hidden="true" />{job.status}</span>
                </div>
                <dl className={s.dl}>
                  {job.rows.map(([k, v]) => (
                    <div key={k} className={s.dlRow}>
                      <dt>{k}</dt>
                      <dd>{v}</dd>
                    </div>
                  ))}
                </dl>
              </section>
              <section className={s.card}>
                <h3 className={s.h3}>{t('box.sameJob')}</h3>
                {job.thread.map(([k, v], i) => (
                  <div key={k} className={`${s.thr} ${i ? s.thrTop : ''}`}>
                    <span>{k}</span>
                    <span>{v}</span>
                  </div>
                ))}
              </section>
            </>
          )}
        </div>
      )}
    </div>
    </Page>
  );
}

function Reader({ m, soon }: { m: BoxMail; soon: () => void }) {
  const t = useT();
  const sla = slaShort(m);
  return (
    <>
      <div className={s.rh}>
        <h2 className={s.rSubj}>{m.subj}</h2>
        <div className={s.rFrom}>
          <button type="button" className={s.rName} onClick={soon}>{m.from}</button> <span className={s.rAddr}>&lt;{m.addr}&gt;</span> · <span className={s.rAddr}>22/Oct/2026 {m.time}</span>
        </div>
        <div className={s.rTo}>{t('box.to')} <span>info@hanalogisticsvn.com</span></div>
        <div className={s.acts} role="toolbar" aria-label="Thao tác thư">
          <button type="button" className={s.askMail} aria-label="Soạn với Ask HANA" onClick={soon}><IconSparkles size={15} stroke={1.9} aria-hidden="true" />Ask HANA<IconChevronDown size={13} stroke={2} aria-hidden="true" /></button>
          <span className={s.grow} />
          <button type="button" className={`${s.act} ${s.actOn}`} aria-label="Trả lời" onClick={soon}><IconArrowBackUp size={16} stroke={1.9} aria-hidden="true" /></button>
          <button type="button" className={s.act} aria-label="Trả lời tất cả" onClick={soon}><IconArrowBackUpDouble size={16} stroke={1.9} aria-hidden="true" /></button>
          <button type="button" className={s.act} aria-label="Chuyển tiếp" onClick={soon}><IconArrowForwardUp size={16} stroke={1.9} aria-hidden="true" /></button>
          <button type="button" className={s.act} aria-label="Follow Up +2 giờ" onClick={soon}><IconAlarm size={16} stroke={1.9} aria-hidden="true" /></button>
          <button type="button" className={s.act} aria-label="Đánh dấu đã xử lý" onClick={soon}><IconCheck size={16} stroke={1.9} aria-hidden="true" /></button>
        </div>
        <div className={s.assign}>
          <span className={s.apills}>
            <span className={s.apill}><IconMail size={14} stroke={1.9} aria-hidden="true" />{m.box}@</span>
            {sla && <span className={s.apill} role="img" aria-label={`SLA ${slaLabel(m)}`}><IconAlarm size={14} stroke={1.9} aria-hidden="true" />{sla}</span>}
            {m.pic !== UNASSIGNED && <span className={`${s.apill} ${s.apillPic}`} role="img" aria-label={`PIC ${m.pic}`}><Avatar pic={m.pic} />{m.pic}</span>}
          </span>
          <button type="button" className={s.giao} onClick={soon}><IconUserPlus size={14} stroke={1.9} aria-hidden="true" />Giao PIC <IconChevronDown size={12} stroke={2} aria-hidden="true" /></button>
        </div>
        <div className={s.tags}>
          {m.label && <span className={s.tagL}><IconTag size={13} stroke={1.9} aria-hidden="true" />{m.label}</span>}
          {m.kh && <span className={s.tagK}><IconBuilding size={13} stroke={1.9} aria-hidden="true" />{m.kh}</span>}
          <a className={s.tagQ} href="/hei/" aria-label="Hỏi nhóm"><IconMessages size={14} stroke={1.9} aria-hidden="true" /></a>
          {m.job && <a className={s.tagJob} href={`/ezy/jobs/${m.job}`} aria-label={`Mở job ${m.job} ở HANA Ezy`}><span>{m.job}</span><IconExternalLink size={13} stroke={1.9} aria-hidden="true" /></a>}
          {m.bk && <span className={s.tagBk}><IconFolderPlus size={13} stroke={1.9} aria-hidden="true" />{m.bk}</span>}
        </div>
        {m.att.length > 0 && (
          <div className={s.atts} role="group" aria-label="Tệp đính kèm">
            {m.att.map((a) => (
              <div key={a.name} className={s.att}>
                <a className={s.attMain} href="#" aria-label={`Xem ${a.name}`} onClick={(e) => { e.preventDefault(); soon(); }}>
                  <span className={s.pdf}>{a.name.endsWith('.pdf') ? 'PDF' : 'XLS'}</span>
                  <span className={s.attName}>{a.name}</span>
                  <span className={s.attKb}>{Math.round((ATT_MB[a.name] ?? 0.1) * 1000)} KB</span>
                </a>
                <button type="button" className={s.attMore} aria-label={`Thao tác với ${a.name}`} onClick={soon}>⋯</button>
              </div>
            ))}
            <div className={s.attBar}>
              <span>{m.att.length} tệp · {attTotal(m, ATT_MB)}</span>
              <button type="button" className={s.attBtn} onClick={soon}><IconDownload size={14} stroke={1.9} aria-hidden="true" />{t('box.downloadAll')}</button>
              <button type="button" className={`${s.attBtn} ${s.attSave}`} onClick={soon}><IconFolderPlus size={14} stroke={1.9} aria-hidden="true" /><span>{t('box.saveAllToJob')}</span></button>
            </div>
          </div>
        )}
      </div>
      <div className={s.body}>
        {m.body.map((p) => <p key={p}><span>{p}</span></p>)}
      </div>
    </>
  );
}
